import { Card } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { TbUserPlus } from "react-icons/tb";
import { Link } from "react-router-dom";
import { RegistrationBodySchema } from "shared";
import z from "zod";
import { LogoUpload } from "@/auth/LogoUpload";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { FormGroup } from "@/components/form/FormGroup";
import { Input } from "@/components/form/Input";
import { LinkGen } from "@/LinkGen";
import { EmptyLayout } from "@/layout/EmptyLayout";

export const RegistrationBodyFrontendSchema = RegistrationBodySchema.extend({
  logo: z.custom<File>((x) => x instanceof File, "Company logo is required"),
});

export type RegistrationFormData = z.infer<
  typeof RegistrationBodyFrontendSchema
>;

export const PageSignUp = () => {
  const { signUp } = useAuth();

  const { register, handleSubmit, formState, control } =
    useForm<RegistrationFormData>({
      resolver: zodResolver(RegistrationBodyFrontendSchema),
    });

  const onSubmit = handleSubmit((values) => {
    return signUp.mutateAsync(values);
  });

  return (
    <EmptyLayout>
      <div style={{ width: "100%", maxWidth: "600px" }}>
        <Card>
          <form onSubmit={onSubmit}>
            <Card.Header>Sign up</Card.Header>
            <Card.Body>
              <fieldset disabled={formState.isSubmitting}>
                <h3 className="mb-3">Company Information</h3>
                <FormGroup
                  label="Company Name"
                  htmlFor="companyName"
                  error={formState.errors.companyName?.message}
                >
                  <Input
                    placeholder="Your company name"
                    id="companyName"
                    {...register("companyName")}
                  />
                </FormGroup>
                <FormGroup
                  label="Company NMLS Number"
                  htmlFor="companyNmls"
                  error={formState.errors.companyNmls?.message}
                >
                  <Input
                    placeholder="Company NMLS number"
                    id="companyNmls"
                    {...register("companyNmls")}
                  />
                </FormGroup>
                <FormGroup
                  label="Company Address"
                  htmlFor="companyAddress"
                  error={formState.errors.companyAddress?.message}
                >
                  <Input
                    placeholder="Company address"
                    id="companyAddress"
                    {...register("companyAddress")}
                  />
                </FormGroup>
                <FormGroup
                  label="Company Logo"
                  htmlFor="logo"
                  error={formState.errors.logo?.message}
                >
                  <LogoUpload control={control} name="logo" />
                </FormGroup>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>
                  Mortgage Professional Information
                </h3>
                <FormGroup
                  label="Name"
                  htmlFor="mpName"
                  error={formState.errors.mpName?.message}
                >
                  <Input
                    placeholder="Your full name"
                    id="mpName"
                    {...register("mpName")}
                  />
                </FormGroup>
                <FormGroup
                  label="Email Address"
                  htmlFor="mpEmail"
                  error={formState.errors.mpEmail?.message}
                >
                  <Input
                    placeholder="Your email address"
                    id="mpEmail"
                    type="email"
                    {...register("mpEmail")}
                  />
                </FormGroup>
                <FormGroup
                  label="Phone Number"
                  htmlFor="mpPhone"
                  error={formState.errors.mpPhone?.message}
                >
                  <Input
                    placeholder="Your phone number"
                    id="mpPhone"
                    type="tel"
                    {...register("mpPhone")}
                  />
                </FormGroup>
                <FormGroup
                  label="NMLS Number"
                  htmlFor="mpNmls"
                  error={formState.errors.mpNmls?.message}
                >
                  <Input
                    placeholder="Your NMLS number"
                    id="mpNmls"
                    {...register("mpNmls")}
                  />
                </FormGroup>
                <FormGroup
                  label="License Number"
                  htmlFor="mpLicenseNumber"
                  error={formState.errors.mpLicenseNumber?.message}
                >
                  <Input
                    placeholder="Your license number"
                    id="mpLicenseNumber"
                    {...register("mpLicenseNumber")}
                  />
                </FormGroup>
              </fieldset>
              <Button
                full
                type="submit"
                loading={formState.isSubmitting}
                icon={TbUserPlus}
              >
                Sign up
              </Button>
              <div style={{ marginTop: "1rem", textAlign: "center" }}>
                Already have an account? <Link to={LinkGen.Auth()}>Login</Link>
              </div>
            </Card.Body>
          </form>
        </Card>
      </div>
    </EmptyLayout>
  );
};
